<template>
  <v-navigation-drawer app clipped :value="value" :permanent="desktop" :temporary="!desktop"
    :mini-variant="collapsed" mini-variant-width="72" width="264" class="app-sidebar" @input="$emit('input', $event)">

    <nav class="app-sidebar__nav">
      <section v-for="section in sections" :key="section.name" class="app-sidebar__section">
        <div v-if="!collapsed" class="app-sidebar__label">{{ section.name }}</div>
        <div v-else class="app-sidebar__divider" />

        <template v-for="item in section.items">
          <!-- Item simples -->
          <v-tooltip v-if="!item.children" :key="item.name" right :disabled="!collapsed" open-delay="150">
            <template v-slot:activator="{ on, attrs }">
              <router-link :to="item.route" class="app-sidebar__item" :class="{ 'is-active': isActive(item) }"
                v-bind="attrs" v-on="on">
                <v-icon size="20">{{ item.icon }}</v-icon>
                <span v-if="!collapsed" class="app-sidebar__text">{{ item.name }}</span>
              </router-link>
            </template>
            <span>{{ item.name }}</span>
          </v-tooltip>

          <!-- Grupo no menu aberto: expande no lugar -->
          <div v-else-if="!collapsed" :key="item.name" class="app-sidebar__group">
            <button type="button" class="app-sidebar__item" :class="{ 'has-active': groupActive(item) }"
              @click="toggleGroup(item)">
              <v-icon size="20">{{ item.icon }}</v-icon>
              <span class="app-sidebar__text">{{ item.name }}</span>
              <v-icon size="18" class="app-sidebar__chevron" :class="{ 'is-open': isOpen(item) }">mdi-chevron-down</v-icon>
            </button>
            <v-expand-transition>
              <div v-show="isOpen(item)" class="app-sidebar__children">
                <router-link v-for="child in item.children" :key="child.route" :to="child.route"
                  class="app-sidebar__child" :class="{ 'is-active': isActive(child) }">
                  {{ child.name }}
                </router-link>
              </div>
            </v-expand-transition>
          </div>

          <!-- Grupo no menu recolhido: lista flutuante ao lado -->
          <v-menu v-else :key="item.name" right offset-x nudge-right="10" open-on-hover :open-delay="80" :close-delay="120"
            content-class="app-sidebar__flyout">
            <template v-slot:activator="{ on, attrs }">
              <div class="app-sidebar__item" :class="{ 'is-active': groupActive(item) }" v-bind="attrs" v-on="on">
                <v-icon size="20">{{ item.icon }}</v-icon>
              </div>
            </template>
            <div class="app-sidebar__flyout-title">{{ item.name }}</div>
            <router-link v-for="child in item.children" :key="child.route" :to="child.route"
              class="app-sidebar__child" :class="{ 'is-active': isActive(child) }">
              {{ child.name }}
            </router-link>
          </v-menu>
        </template>
      </section>
    </nav>

    <template v-if="desktop" v-slot:append>
      <button type="button" class="app-sidebar__collapse" :title="collapsed ? 'Expandir menu' : 'Recolher menu'"
        @click="$emit('toggle-mini')">
        <v-icon size="20">{{ collapsed ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left' }}</v-icon>
        <span v-if="!collapsed">Recolher menu</span>
      </button>
    </template>
  </v-navigation-drawer>
</template>

<script>
import sidebarMenu from '@/common/data/sidebar-menu.data'

export default {
  name: 'AppSidebar',

  props: {
    value: { type: Boolean, default: false },
    mini: { type: Boolean, default: false },
    desktop: { type: Boolean, default: true }
  },

  data: () => ({
    // Grupos abertos/fechados pelo usuário (o grupo da tela atual abre sozinho)
    manualOpen: {}
  }),

  computed: {
    collapsed() {
      return this.desktop && this.mini
    },

    // Seções e itens já filtrados pelas permissões do usuário
    sections() {
      return sidebarMenu
        .map(section => ({
          name: section.name,
          items: section.children
            .map(item => item.children
              ? { ...item, children: item.children.filter(child => this.$check(child.permissions)) }
              : item)
            .filter(item => item.children ? item.children.length : this.$check(item.permissions))
        }))
        .filter(section => section.items.length)
    },

    allRoutes() {
      const routes = []
      this.sections.forEach(section => section.items.forEach(item => {
        if (item.route) routes.push(item.route)
        ;(item.children || []).forEach(child => routes.push(child.route))
      }))
      return routes
    }
  },

  methods: {
    matches(route) {
      const path = this.$route.path
      return path === route || path.startsWith(route + '/')
    },

    // Ativo quando a rota bate e nenhum outro item mais específico também bate
    // ("/financeiro" não fica ativo dentro de "/financeiro/motoristas")
    isActive(item) {
      if (!item.route || !this.matches(item.route)) return false
      return !this.allRoutes.some(route => route.length > item.route.length && route.startsWith(item.route) && this.matches(route))
    },

    groupActive(item) {
      return (item.children || []).some(child => this.isActive(child))
    },

    isOpen(item) {
      if (this.manualOpen[item.name] !== undefined) return this.manualOpen[item.name]
      return this.groupActive(item)
    },

    toggleGroup(item) {
      this.$set(this.manualOpen, item.name, !this.isOpen(item))
    }
  }
}
</script>

<style lang="scss" scoped>
.app-sidebar {
  background: #ffffff !important;
  border-right: 1px solid var(--v-border-base);

  ::v-deep .v-navigation-drawer__border {
    display: none;
  }

  ::v-deep .v-navigation-drawer__content {
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;

    &:hover {
      scrollbar-color: #cfd9e8 transparent;
    }

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 3px;
    }

    &:hover::-webkit-scrollbar-thumb {
      background: #cfd9e8;
    }
  }
}

.app-sidebar__nav {
  padding: 12px 12px 8px;
}

.app-sidebar__section + .app-sidebar__section {
  margin-top: 14px;
}

.app-sidebar__label {
  padding: 0 10px 6px;
  color: #8a96ab;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}

.app-sidebar__divider {
  height: 1px;
  margin: 0 8px 10px;
  background: var(--v-border-base);
}

.app-sidebar__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 40px;
  padding: 0 10px;
  margin-bottom: 2px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #33415c;
  font-size: 13.5px;
  font-weight: 500;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;

  .v-icon {
    color: #6b7a93;
    flex-shrink: 0;
  }

  &:hover {
    background: #f1f5fb;
    color: #10213f;

    .v-icon {
      color: #10213f;
    }
  }

  &.is-active {
    background: #e7f0ff;
    color: var(--v-primary-base);
    font-weight: 600;

    .v-icon {
      color: var(--v-primary-base);
    }

    &::before {
      content: '';
      position: absolute;
      left: -12px;
      top: 8px;
      bottom: 8px;
      width: 3px;
      border-radius: 0 3px 3px 0;
      background: var(--v-primary-base);
    }
  }

  &.has-active {
    color: #10213f;
    font-weight: 600;

    .v-icon {
      color: var(--v-primary-base);
    }
  }
}

.app-sidebar__text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-sidebar__chevron {
  transition: transform 0.2s ease;

  &.is-open {
    transform: rotate(180deg);
  }
}

.app-sidebar__children {
  position: relative;
  margin: 2px 0 6px 20px;
  padding-left: 14px;
  border-left: 1px solid var(--v-border-base);
}

.app-sidebar__child {
  display: block;
  padding: 7px 10px;
  margin-bottom: 1px;
  border-radius: 6px;
  color: #52627d;
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: #f1f5fb;
    color: #10213f;
  }

  &.is-active {
    background: #e7f0ff;
    color: var(--v-primary-base);
    font-weight: 600;
  }
}

.app-sidebar__collapse {
  display: flex;
  align-items: center;
  gap: 12px;
  width: calc(100% - 24px);
  margin: 8px 12px 12px;
  padding: 9px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #6b7a93;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f1f5fb;
    color: #10213f;
  }

  .v-icon {
    color: inherit;
  }
}

.v-navigation-drawer--mini-variant {
  .app-sidebar__nav {
    padding: 12px 10px 8px;
  }

  .app-sidebar__item {
    justify-content: center;
    padding: 0;

    &.is-active::before {
      left: -10px;
    }
  }

  .app-sidebar__collapse {
    justify-content: center;
    width: calc(100% - 20px);
    margin: 8px 10px 12px;
  }
}
</style>

<style lang="scss">
// Lista flutuante do grupo no menu recolhido (renderizada fora do drawer)
.app-sidebar__flyout {
  min-width: 210px;
  padding: 8px;
  background: #ffffff;
  border: 1px solid var(--v-border-base);
  border-radius: 10px !important;
  box-shadow: 0 12px 32px rgba(16, 33, 63, 0.14) !important;

  .app-sidebar__flyout-title {
    padding: 6px 10px 8px;
    color: #8a96ab;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .app-sidebar__child {
    display: block;
    padding: 8px 10px;
    border-radius: 6px;
    color: #33415c;
    font-size: 13px;
    text-decoration: none;

    &:hover {
      background: #f1f5fb;
    }

    &.is-active {
      background: #e7f0ff;
      color: var(--v-primary-base);
      font-weight: 600;
    }
  }
}
</style>
